import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Field, FileField, Checkbox, Btn, DropdownField, Action, NumberField, RangeField, FormButtonDisabler, IModalComponent, DocumentService, DataImportService, StagingService, IExcelTab, IDocument } from '@samc/centricity-core';
import { MappingSource } from '@/Sources';
import { MappingDropdownField } from '@/Components/Dropdowns';
import { AjaxHelper, errToMessage, getToken, Guid } from '@samc/common';
import { IMatchedLoans } from '@/Components/Steps/Interfaces';
import { gotoPost } from '@/utils';

type ImportType = 'Template' | 'Mapping';

@Component({
  name: 'simple-internal-data-import-modal',
  components: { Field, FileField, Checkbox, Btn, Action, NumberField, RangeField, DropdownField, FormButtonDisabler, MappingDropdownField }
})
export default class SimpleInternalDataImportModal extends Vue implements IModalComponent {
  @Prop() public addCloseProp!: (callback: () => Promise<null>) => void;
  @Prop() public closeProp!: () => void;
  @Prop() public url!: string;
  @Prop({ default: () => null }) public dataImportUrl!: string | null;
  @Prop({ default: () => null }) public documentUrl!: string | null;
  @Prop() public verificationToken!: string;
  @Prop() public user!: string;

  public importProcessId = 18;
  public documentService: DocumentService | null = null;
  public dataImportService: DataImportService | null = null;
  public stagingService: StagingService | null = null;
  public ajaxHelper: AjaxHelper | null = null;

  public importType: ImportType = 'Template';
  public file: File | null = null;
  public keepNull: boolean = false;
  public shouldValidate: boolean = false;
  public validations: Record<string, any> | null = null;
  public mappingId: string = '';
  public tabIndex: number = 0;
  public headerRowMinValue: number = 1;
  public headerRowMaxValue: number = 1;
  public dataRow: number = 2;
  public sourceId: string = '';

  public importTypes: ImportType[] = ['Template', 'Mapping'];
  public mappings: MappingSource | any[] = [];
  public excelTabs: IExcelTab[] = [];

  public document: IDocument | null = null;

  public get isValid(): boolean { return this.validations === null || Object.keys(this.validations).length === 0; }

  public saving: boolean = false;

  public mounted() {
    this.documentService = new DocumentService(new AjaxHelper(this.documentUrl ?? this.url, this.verificationToken, null, getToken()));
    this.dataImportService = new DataImportService(new AjaxHelper(this.dataImportUrl ?? this.url, this.verificationToken, null, getToken()));
    this.stagingService = new StagingService(new AjaxHelper(this.url, this.verificationToken, null, getToken()));
    this.ajaxHelper = new AjaxHelper(this.url, this.verificationToken, null, getToken());

    this.mappings = new MappingSource({ importProcessId: this.importProcessId });

    this.addCloseProp(async () => null);
  }

  public getValidationErrorText(key: string) {
    return this.validations?.[key] ?? '';
  }

  public async submitModal() {
    this.shouldValidate = true;

    this.checkValidation();
    if (!this.isValid) return;

    switch (this.importType) {
      case 'Mapping':
        await this.saveMappedInternalData();
        break;
      case 'Template':
        await this.saveTemplatedInternalData();
        break;
    }
    this.closeProp();
  }

  @Watch('file')
  public async onFileChange(newFile: File) {
    if (this.dataImportService !== null && this.documentService !== null) {
      try {
        this.$loading('Uploading File...');
        const mimetype = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        this.document = await this.documentService.createDocument(newFile, Guid.create().toString(), mimetype, 'Simple Internal Data Import Template');

        this.$loading('Getting Excel Tabs...');
        this.excelTabs = await this.dataImportService.getExcelFileTabs(this.document.documentId);
      } catch (err) {
        this.$log(err);
        this.$alert({
          type: 'error',
          title: 'Failed to import template',
          description: 'For more detailed information, use the advanced internal data import',
          errors: errToMessage(err),
          closingOption: 'holdOpen'
        });
      } finally {
        this.$loading();
        this.saving = false;
      }
    }
  }

  @Watch('file')
  @Watch('mappingId')
  public checkValidation() {
    if (!this.shouldValidate) return;

    const newValidations: Record<string, string> = {};
    if (this.file === null)
      newValidations['file'] = 'Please select a file';
    if (this.tabIndex <= -1)
      newValidations['tabIndex'] = 'Please select a tab';
    if (this.headerRowMinValue <= 0)
      newValidations['haderRow'] = 'Cannot be less than 1';
    if (this.headerRowMaxValue < this.headerRowMinValue)
      newValidations['haderRow'] = 'Cannot be less than header row min';
    if (this.dataRow <= this.headerRowMaxValue)
      newValidations['dataRow'] = 'Cannot be less than header';
    if (this.dataRow <= 0)
      newValidations['dataRow'] = 'Cannot be less than 1';

    if (this.importType === 'Mapping' && this.mappingId === '')
      newValidations['mappingId'] = 'Please select a mapping';
    this.validations = newValidations;
  }

  public async askToGotoSurvivorship(updateCount: number | null = null) {
    if (this.keepNull) {
      const result = await this.$confirm({
        type: 'success',
        title: 'Template imported successfully',
        description: 'Would you like to go to survivorship?',
        button: {
          text: 'Go To Targeted Survivorship', data: true
        }
      });
      if (result === true)
        return await this.gotoSurvivorship();

      if (updateCount !== null)
        this.$alert.success(`Template imported successfully with ${updateCount} rows updated.`);
    }
  }

  public async gotoSurvivorship() {
    gotoPost('/DataImport/Survivorship', this.verificationToken, { sourceId: this.sourceId, goldenIsNullOption: 'highestNonNull', goldenIsNotNullOption: 'highestNonNull' });
  }

  public async saveMappedInternalData() {
    if (this.documentService !== null && this.dataImportService !== null && this.stagingService !== null && this.ajaxHelper !== null && this.document !== null) {
      try {
        this.saving = true;
        const extraParams: Record<string, string> = { importProcessId: this.importProcessId.toString() };

        this.$loading('Create File Source...');
        const result = await this.dataImportService.addExcelFileRawData(
          this.document.documentId,
          this.tabIndex,
          '',
          this.document.fileName,
          this.user,
          this.headerRowMinValue,
          this.dataRow,
          this.headerRowMinValue,
          this.headerRowMaxValue,
          extraParams
        );

        // There is no way to update the mapping id on the source on creation so we need to update it
        result.file.mappingId = this.mappingId;
        await this.dataImportService.updateFileSource(result.file, extraParams);

        this.$loading('Creating Staging Data...');
        const stagingResponse = await this.stagingService.createPreview(result.rawMetaData.id, this.mappingId, result.file.id, extraParams);
        if (stagingResponse.length > 0)
          throw new Error(`Data import failed with ${stagingResponse.length} type conversion errors`);

        this.$loading('Matching Loans...');
        const matchResponse = await this.ajaxHelper.getData<IMatchedLoans>(`/matchedLoans/${result.file.id}`);
        const matchedLoans = matchResponse.loanItemDetails.filter(x => x.isFound == true);
        if (matchedLoans.length === 0)
          throw new Error('No matched loans found');

        this.$loading('Importing Data...');
        const importResponse = await this.stagingService.importStaging({
          packageId: '',
          rawDataId: result.rawMetaData.id,
          mappingId: this.mappingId,
          sourceId: result.file.id,
          versionName: 'Simple Internal Data Import',
          addFlag: false,
          updateFlag: false,
          deleteFlag: false,
          replaceFlag: false,
          mergeFlag: false,
          keepNullFlag: this.keepNull,
          promoteToGoldenFlag: false,
          replaceAllFieldsGoldenFlag: false,
          promoteNew2G: false
        }, extraParams);

        if (!this.keepNull)
          this.$alert.success(`Template imported successfully with ${importResponse.countDetails.updateCount} rows updated.`);
        this.sourceId = result.file.id;

        await this.askToGotoSurvivorship(importResponse.countDetails.updateCount);
      } catch (err) {
        this.$log(err);
        this.$alert({
          type: 'error',
          title: 'Failed to import template',
          description: 'For more detailed information, use the advanced internal data import',
          errors: errToMessage(err),
          closingOption: 'holdOpen'
        });
      } finally {
        this.$loading();
        this.saving = false;
      }
    }
  }

  public async saveTemplatedInternalData() {
    if (this.documentService !== null && this.dataImportService !== null && this.document !== null) {
      try {
        this.saving = true;

        this.$loading('Create File Source...');
        const result = await this.dataImportService.addExcelFileRawData(
          this.document.documentId,
          this.tabIndex,
          '',
          this.document.fileName,
          this.user,
          this.headerRowMinValue,
          this.dataRow,
          this.headerRowMinValue,
          this.headerRowMaxValue,
          { importProcessId: '19' }
        );

        this.$loading('Importing Template...');
        const request = { sourceId: result.file.id, metaId: result.rawMetaData.id, user: this.user, keepNull: this.keepNull };
        const response = await this.ajaxHelper?.postData<number>('internal/data/template/import', request);

        this.$alert.success(`Template imported successfully with ${response} rows updated.`);
        this.sourceId = result.file.id;

        await this.askToGotoSurvivorship();
      } catch (err) {
        this.$log(err);
        this.$alert({
          type: 'error',
          title: 'Failed to import template',
          description: 'For more detailed information, use the advanced internal data import',
          errors: errToMessage(err),
          closingOption: 'holdOpen'
        });
      } finally {
        this.$loading();
        this.saving = false;
      }
    }
  }
}